import React, { useEffect, useRef, useState } from 'react';
import './QuestionTextEditorModal.css';
import { Bold, Image as ImageIcon, Italic, Underline, X } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { ColorResult, SketchPicker } from 'react-color';

interface Props {
  initialText: string;
  onSave: (text: string) => void;
  onClose: () => void;
  dir?: 'rtl' | 'ltr';
}

const fontSizes = ['12', '14', '16', '18', '20', '24', '28', '32'];

const QuestionTextEditorModal: React.FC<Props> = ({
  initialText,
  onSave,
  onClose,
  dir = 'rtl',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState('16');
  const [fontColor, setFontColor] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);

  useEffect(() => {
    const handleDelete = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement;

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        active?.classList?.contains('qt-image-wrapper')
      ) {
        e.preventDefault(); // prevent page scroll
        const parent = active.parentElement;
        if (parent) {
          parent.removeChild(active);
        }
      }
    };

    document.addEventListener('keydown', handleDelete);
    return () => document.removeEventListener('keydown', handleDelete);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = sanitizeHtml(initialText);
    }
  }, [initialText]);

  const applyStyle = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const applyFontSize = (size: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const span = document.createElement('span');
    span.style.fontSize = size + 'px';
    span.appendChild(range.extractContents());
    range.deleteContents();
    range.insertNode(span);
    setFontSize(size);
    editorRef.current?.focus();
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0).cloneRange());
    }
  };

  const restoreSelection = () => {
    if (savedSelection) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection);
      }
    }
  };

  const handleColorChange = (color: ColorResult) => {
    const newColor = color.hex;
    setFontColor(newColor);
    if (savedSelection) {
      restoreSelection();
      document.execCommand('foreColor', false, newColor);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function (event) {
      const img = document.createElement('img');
      img.src = event.target?.result as string;
      img.alt = 'uploaded';
      img.className = 'qt-inserted-image';
      img.style.maxWidth = '100%';

      // Focus editor to restore selection
      editorRef.current?.focus();

      // Defer the DOM insertion slightly to ensure the focus is applied
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(img);

        // Move cursor after image
        range.setStartAfter(img);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }, 0);
    };
    reader.readAsDataURL(file);

    // Reset the input so user can upload the same image again
    e.target.value = '';
  };

  const handleSave = () => {
    const html = editorRef.current?.innerHTML || '';
    onSave(html);
  };

  return (
    <div className='qt-modal-overlay'>
      <div className='qt-modal' dir='rtl'>
        <div className='qt-modal-header'>
          <h3>עריכת טקסט</h3>
          <button className='qt-close-btn' onClick={onClose}>
            <X />
          </button>
        </div>

        <div className='qt-toolbar'>
          <button onClick={() => applyStyle('bold')}>
            <Bold />
          </button>
          <button onClick={() => applyStyle('italic')}>
            <Italic />
          </button>
          <button onClick={() => applyStyle('underline')}>
            <Underline />
          </button>

          <select
            value={fontSize}
            onChange={(e) => applyFontSize(e.target.value)}
            className='qt-font-size-select'
          >
            {fontSizes.map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>

          <div className='qt-color-picker-container' ref={colorPickerRef}>
            <button
              onClick={() => {
                saveSelection();
                setShowColorPicker(!showColorPicker);
              }}
              className='qt-color-picker-button'
              title='בחר צבע טקסט'
            >
              <div className='qt-color-preview' style={{ backgroundColor: fontColor }} />
            </button>

            {showColorPicker && (
              <div className='qt-color-picker-popover'>
                <SketchPicker color={fontColor} onChange={handleColorChange} />
                <div className='qt-color-picker-actions'>
                  <button
                    className='qt-color-picker-save-btn'
                    onClick={() => setShowColorPicker(false)}
                  >
                    בחר
                  </button>
                  <button
                    className='qt-color-picker-cancel-btn'
                    onClick={() => setShowColorPicker(false)}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            )}
          </div>

          <label className='qt-image-upload-btn'>
            <ImageIcon />
            <input type='file' accept='image/*' onChange={handleImageUpload} hidden />
          </label>
        </div>

        <div className='qt-editor' contentEditable dir={dir} ref={editorRef} />

        <div className='qt-modal-footer'>
          <button className='qt-save-btn' onClick={handleSave}>
            שמור
          </button>
          <button className='qt-cancel-btn' onClick={onClose}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionTextEditorModal;
